/**
 * notes-api.js
 * Pure Supabase CRUD for the notes table.
 * No business logic — call only from notes-service or notes-sync.
 *
 * Table schema (Supabase):
 *   id                 uuid primary key
 *   district_id        uuid  references districts(id)
 *   title              text
 *   content            text
 *   tags               text[]  default '{}'
 *   linked_type        text    default 'general'  -- 'general'|'person'|'family'|'church'|'event'|'preaching'|'visit'
 *   linked_id          uuid
 *   linked_label       text    -- denormalized display name for offline
 *   category           text    default 'general'  -- 'general'|'espiritual'|'pastoral'|'administrativo'|'personal'
 *   visibility         text    default 'private'  -- 'private'|'district'
 *   is_reference       boolean default false
 *   pinned             boolean default false      -- legacy, prefer is_reference
 *   archived           boolean default false
 *   sync_status        text    default 'synced'
 *   dirty              boolean default false
 *   created_at         timestamptz default now()
 *   updated_at         timestamptz default now()
 *   updated_locally_at timestamptz
 *   last_viewed_at     timestamptz
 *
 * RLS: district_id must match user's district.
 */

import { supabase } from './supabase';

const TABLE = 'notes';

/**
 * Fetch notes for a district, newest first.
 * @param {string} districtId
 * @param {{ isCoordinator?: boolean, search?: string }} options
 * @returns {Promise<Object[]>}
 */
export async function fetchNotes(districtId, { isCoordinator = false, search = null } = {}) {
  let query = supabase
    .from(TABLE)
    .select('*')
    .eq('district_id', districtId)
    .order('updated_at', { ascending: false })
    .limit(100);

  if (isCoordinator) {
    query = query.neq('visibility', 'private');
  }

  if (search?.trim()) {
    const term = `%${search.trim()}%`;
    query = query.or(`title.ilike.${term},content.ilike.${term}`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Create a note in Supabase.
 * Accepts a client-generated UUID in `note.id` for offline-first flows.
 * @param {Object} note
 * @returns {Promise<Object>} created row
 */
export async function createNote(note) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(note)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update a note's mutable fields.
 * Always stamps `updated_at` server-side.
 * @param {string} id
 * @param {Object} updates
 * @returns {Promise<Object>} updated row
 */
export async function updateNote(id, updates) {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Hard-delete a note from Supabase.
 * @param {string} id
 */
export async function deleteNote(id) {
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id);

  if (error) throw error;
}
